import { useState } from 'react';
import { Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../../../shared/components/PageHeader';
import { DataTable, type DataTableQuery } from '../../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../../shared/grid/usePersistedGridState';
import { IncludeInactiveToggle } from '../../../../shared/components/IncludeInactiveToggle';
import { ConfirmDialog } from '../../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../../shared/components/ErrorState';
import { useToast } from '../../../../shared/stores/toast.store';
import { describeApiError } from '../../../../shared/api/errors';
import { impostosHooks } from '../hooks';
import { impostosApi } from '../api';
import { ImpostoFormDialog } from '../ImpostoFormDialog';
import type { ImpostoDto, ImpostoListItem } from '../types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'nome',
  sortOrder: 'asc',
};

/**
 * `aliquota` já é percentual direto (3 = 3%). Apenas formata com `,` decimal
 * e sufixo `%`. Não multiplicar/dividir por 100 — toda a stack GeneXus segue
 * essa convenção.
 */
const formatPercent = (value: number): string =>
  `${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

export function ImpostosListPage(): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { query, setQuery, clear } = usePersistedGridState('impostos', DEFAULT_QUERY, {});
  const [editing, setEditing] = useState<ImpostoDto | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<ImpostoListItem | null>(null);

  const listQuery = impostosHooks.useList(query);
  const removeMutation = impostosHooks.useDelete();

  async function openEdit(row: ImpostoListItem): Promise<void> {
    try {
      setEditing(await impostosApi.get(row.id));
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await removeMutation.mutateAsync(pendingDelete.id);
      toast.success(t('features.impostos.toasts.deleted'));
      setPendingDelete(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<ImpostoListItem>[] = [
    { field: 'nome', headerName: t('features.impostos.columns.nome'), flex: 1, minWidth: 220 },
    { field: 'tipo', headerName: t('features.impostos.columns.tipo'), width: 100 },
    {
      field: 'aliquota',
      headerName: t('features.impostos.columns.aliquota'),
      width: 120,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number) => formatPercent(value),
    },
    {
      field: 'empresaNome',
      headerName: t('features.impostos.columns.empresa'),
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'ativo',
      headerName: t('features.impostos.columns.ativo'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={
            params.value
              ? t('features.impostos.status.active')
              : t('features.impostos.status.inactive')
          }
          color={params.value ? 'success' : 'default'}
          size="small"
          variant={params.value ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              aria-label={t('features.impostos.actions.edit', { nome: params.row.nome })}
              onClick={() => void openEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton
              size="small"
              color="error"
              aria-label={t('features.impostos.actions.delete', { nome: params.row.nome })}
              onClick={() => setPendingDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <>
        <PageHeader title={t('menu.taxes')} />
        <ErrorState
          description={describeApiError(listQuery.error)}
          onRetry={() => listQuery.refetch()}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('menu.taxes')}
        breadcrumbs={[{ label: t('menu.generalRegistries') }, { label: t('menu.taxes') }]}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
            {t('features.impostos.actions.new')}
          </Button>
        }
      />
      <DataTable<ImpostoListItem>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.impostos.searchPlaceholder')}
        filters={<IncludeInactiveToggle query={query} onChange={setQuery} />}
      />
      <ImpostoFormDialog
        open={editing !== null}
        imposto={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />
      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.impostos.deleteConfirm.title')}
        description={t('features.impostos.deleteConfirm.description', {
          nome: pendingDelete?.nome ?? '',
        })}
        confirmLabel={t('common.delete')}
        loading={removeMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
